import React, { useState, useEffect } from 'react';
import { toast } from '../Toast';
import {
  Settings, Zap, Trash2,
  RefreshCw, Database, Download, Upload,
  ShieldAlert, Save
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { apiFetch, downloadFile } from '../../services/apiClient';
import PageHeader from '../ui/PageHeader';
import { ControlBar, ToggleGroup } from '../ui/ControlBar';

// NOTE: The previously-hardcoded DASHBOARD_CONSTRAINTS and ON_CALL_CONSTRAINTS
// arrays have been removed. The constraint list is now fetched live from the
// backend (which reads the same constraints.yaml the maintenance admin GUI
// writes to), so this page always mirrors whatever the maintenance team has
// currently configured — including department-specific constraints that did
// not exist when this file was written.
//
// The fetched shape per constraint is:
//   { id, scope, label, type: "hard"|"soft", desc, enabled, tier }
// mapped from each YAML entry as:
//   display_name -> label
//   description  -> desc
//   weight === 0 -> type "hard" (else "soft")
//   enabled      -> on/off status (read-only on this page)
//
// Toggling is intentionally NOT available here — constraints are managed
// exclusively through the token-gated maintenance admin GUI. This page is a
// read-only mirror. See ConstraintCard below (rendered with readOnly).

// Read-only endpoint served by the ops backend (behind apiClient), which
// reads constraints.yaml and returns the lightweight display shape above.
const CONSTRAINTS_ENDPOINT = '/api/constraints/active';

const SettingsTab = ({ onOnCallCleared, onRosterFreeRowsChange, 
  schedule, loadDemoData 
}) => {
  const [viewMode, setViewMode] = useState('optimization');
  const [isClearing, setIsClearing] = useState(null);

  // --- LIVE CONSTRAINT LIST (fetched from backend, read-only) ---
  const [clinicalConstraints, setClinicalConstraints] = useState([]);
  const [onCallConstraints, setOnCallConstraints] = useState([]);
  const [constraintsLoading, setConstraintsLoading] = useState(true);
  const [constraintsError, setConstraintsError] = useState(null);

  // Solver time limit (admin-editable guardrail). Stored in seconds; shown in
  // minutes. Bounds come from the backend so the input reflects real limits.
  const [solverLimit, setSolverLimit] = useState(null);        // seconds
  const [solverBounds, setSolverBounds] = useState({ min: 5, max: 3600, default: 600 });
  const [solverSaving, setSolverSaving] = useState(false);
  const [solverStatus, setSolverStatus] = useState(null);      // 'saved' | 'error' | null

  // Staff workload counter: which metric the Final Roster staff view shows.
  // Global display toggle ('shifts' | 'hours'); both are computed either way.
  const [counterMetric, setCounterMetric] = useState('shifts');
  const [counterSaving, setCounterSaving] = useState(false);
  const [counterStatus, setCounterStatus] = useState(null);

  // Final Roster day rows: whole-day OFF (default), or main's HRM layout of
  // FREE per half-day plus POST-CALL. Display only; also shapes the Excel.
  const [freeRows, setFreeRows] = useState(false);
  const [freeRowsStatus, setFreeRowsStatus] = useState(null);

  // Count leave into the workload counter, per metric. Each metric has its own
  // toggle and its own value; the card below configures whichever metric is
  // selected above. Display only — the solver is untouched (see
  // DataService.get_leave_counter_settings).
  const [leaveCounter, setLeaveCounter] = useState({
    shifts: { enabled: false, value: 1 },
    hours: { enabled: false, value: 8 },
  });
  const [leaveDraft, setLeaveDraft] = useState(null);   // in-flight edit of the number field
  const [leaveMax, setLeaveMax] = useState({ shifts: 10, hours: 24 });
  const [leaveMin, setLeaveMin] = useState(0);
  const [leaveSaving, setLeaveSaving] = useState(false);
  const [leaveStatus, setLeaveStatus] = useState(null);

  useEffect(() => {
    apiFetch('/api/solver-settings')
      .then(r => r.json())
      .then(d => {
        if (d && d.success) {
          setSolverLimit(d.solver_time_limit_seconds);
          setSolverBounds({ min: d.min_seconds, max: d.max_seconds, default: d.default_seconds });
          if (d.staff_counter_metric) setCounterMetric(d.staff_counter_metric);
          if (typeof d.roster_free_rows === 'boolean') setFreeRows(d.roster_free_rows);
          if (d.leave_counter) setLeaveCounter(d.leave_counter);
          if (d.leave_counter_max) setLeaveMax(d.leave_counter_max);
          if (Number.isFinite(d.leave_counter_min)) setLeaveMin(d.leave_counter_min);
        }
      })
      .catch(() => {});
  }, []);

  // One writer for both fields — the toggle sends only `enabled`, the number
  // field only `value`, so neither can clobber the other in flight. Always
  // carries the metric: the two are configured independently.
  const saveLeaveCounter = async (patch) => {
    setLeaveSaving(true); setLeaveStatus(null);
    try {
      const res = await apiFetch('/api/leave-counter', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metric: counterMetric, ...patch }),
      });
      const d = await res.json();
      if (!res.ok || d.success === false) { setLeaveStatus('error'); return; }
      if (d.leave_counter) setLeaveCounter(d.leave_counter);
      setLeaveDraft(null);
      setLeaveStatus('saved');
      setTimeout(() => setLeaveStatus(null), 2500);
    } catch { setLeaveStatus('error'); }
    finally { setLeaveSaving(false); }
  };

  const saveCounterMetric = async (metric) => {
    setCounterMetric(metric);              // optimistic
    setCounterSaving(true); setCounterStatus(null);
    try {
      const res = await apiFetch('/api/staff-counter-metric', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff_counter_metric: metric }),
      });
      const d = await res.json();
      if (!res.ok || d.success === false) { setCounterStatus('error'); return; }
      setCounterStatus('saved');
      setTimeout(() => setCounterStatus(null), 2500);
    } catch { setCounterStatus('error'); }
    finally { setCounterSaving(false); }
  };

  const saveFreeRows = async (on) => {
    setFreeRows(on);                       // optimistic
    setFreeRowsStatus(null);
    try {
      const res = await apiFetch('/api/roster-free-rows', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roster_free_rows: on }),
      });
      const d = await res.json();
      if (!res.ok || d.success === false) { setFreeRows(!on); setFreeRowsStatus('error'); return; }
      if (onRosterFreeRowsChange) onRosterFreeRowsChange(on);
      setFreeRowsStatus('saved');
      setTimeout(() => setFreeRowsStatus(null), 2500);
    } catch { setFreeRows(!on); setFreeRowsStatus('error'); }
  };

  const saveSolverLimit = async () => {
    setSolverSaving(true); setSolverStatus(null);
    try {
      const res = await apiFetch('/api/solver-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ solver_time_limit_seconds: solverLimit }),
      });
      const d = await res.json();
      if (!res.ok || d.success === false) { setSolverStatus('error'); return; }
      setSolverLimit(d.solver_time_limit_seconds);
      setSolverStatus('saved');
      setTimeout(() => setSolverStatus(null), 2500);
    } catch { setSolverStatus('error'); }
    finally { setSolverSaving(false); }
  };

  // Fetch the current constraint configuration on mount. The backend reads the
  // same constraints.yaml the maintenance admin GUI writes to, so this list
  // always reflects the live maintenance configuration.
  useEffect(() => {
    let cancelled = false;
    const loadConstraints = async () => {
      setConstraintsLoading(true);
      setConstraintsError(null);
      try {
        const res = await apiFetch(CONSTRAINTS_ENDPOINT);
        const data = await res.json();
        if (cancelled) return;
        // Split by scope; the backend returns one flat list for both solvers.
        setClinicalConstraints(data.filter(c => c.scope === 'clinical'));
        setOnCallConstraints(data.filter(c => c.scope === 'on_call'));
      } catch (e) {
        if (!cancelled) setConstraintsError('Could not load constraint configuration.');
        console.error(e);
      } finally {
        if (!cancelled) setConstraintsLoading(false);
      }
    };
    loadConstraints();
    return () => { cancelled = true; };
  }, []);

  // --- SYSTEM MAINTENANCE HANDLERS ---
  const handleGlobalReset = async (type) => {
    if (!window.confirm(`Are you sure you want to clear all ${type}? This action cannot be undone.`)) return;
    setIsClearing(type);
    try {
      let endpoint = '';
      if (type === 'leaves' || type === 'availability') endpoint = '/api/maintenance/clear-leaves';
      else if (type === 'on-call') endpoint = '/api/maintenance/clear-on-call';
      
      const res = await apiFetch(endpoint, { method: 'POST' });
      if (res.ok) {
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} cleared.`);
        // The console holds its own copy of the on-call roster; drop it, or the
        // next edit would publish the cleared roster straight back.
        if (type === 'on-call' && onOnCallCleared) onOnCallCleared();
        loadDemoData('SMALL');
      }
    } catch (e) { console.error(e); }
    finally { setIsClearing(null); }
  };

  return (
    <div className="animate-in fade-in space-y-6 pb-20">
      
      {/* FLOATING IDENTITY */}
      <PageHeader icon={Settings} title="System Settings" />

      <ControlBar>
        <ToggleGroup label="View" value={viewMode} onChange={setViewMode}
          options={[
            { id: 'optimization', label: 'Solver Logic', icon: Zap },
            { id: 'maintenance', label: 'System Tools', icon: Database },
          ]} />
      </ControlBar>

      {/* 1. OPTIMIZATION ENGINE SECTION */}
      {viewMode === 'optimization' && (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">

          {/* Editable: solver time ceiling (guardrail for large windows) */}
          <Card padding="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="max-w-lg">
                <h4 className="text-sm font-black text-slate-900 tracking-tight">Solver Time Limit</h4>
                <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed">
                  How long a solve may run. Longer gives better rosters for big windows, like a month.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <input
                    type="number"
                    min={Math.ceil((solverBounds.min || 5) / 60)}
                    max={Math.floor((solverBounds.max || 3600) / 60)}
                    value={solverLimit != null ? Math.round(solverLimit / 60) : ''}
                    onChange={(e) => {
                      const mins = parseInt(e.target.value, 10);
                      setSolverStatus(null);
                      setSolverLimit(Number.isFinite(mins) ? mins * 60 : null);
                    }}
                    className="w-16 text-center text-sm font-bold text-slate-800 outline-none"
                  />
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">min</span>
                </div>
                <Button onClick={saveSolverLimit} icon={Save} isLoading={solverSaving} disabled={solverSaving || solverLimit == null} className="h-[38px] px-5 text-xs font-bold">Save</Button>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest">
              <span className="text-slate-300">{Math.ceil((solverBounds.min || 5) / 60)}–{Math.floor((solverBounds.max || 3600) / 60)} min · default {Math.round((solverBounds.default || 600) / 60)}</span>
              {solverStatus === 'saved' && <span className="text-emerald-600">Saved</span>}
              {solverStatus === 'error' && <span className="text-rose-500">Invalid value</span>}
            </div>
          </Card>

          {/* Editable: staff workload counter metric (display toggle) */}
          <Card padding="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="max-w-lg">
                <h4 className="text-sm font-black text-slate-900 tracking-tight">Staff Workload Counter</h4>
                <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed">
                  What the Final Roster&rsquo;s staff view counts for each person.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                  {[['shifts', 'Shifts'], ['hours', 'Hours']].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      disabled={counterSaving}
                      onClick={() => saveCounterMetric(val)}
                      className={`px-4 h-[34px] rounded-lg text-xs font-bold transition-all ${counterMetric === val ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                    >{lbl}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest">
              {counterStatus === 'saved' && <span className="text-emerald-600">Saved</span>}
              {counterStatus === 'error' && <span className="text-rose-500">Save failed</span>}
            </div>
          </Card>

          {/* Final Roster day rows: OFF, or FREE + POST-CALL (main's HRM layout). */}
          <Card padding="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="max-w-lg">
                <h4 className="text-sm font-black text-slate-900 tracking-tight">Who Is Free</h4>
                <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed">
                  How the Final Roster and Excel show people who aren&rsquo;t working: by whole day,
                  or by morning and afternoon with a post-call row.
                </p>
              </div>
              <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                {[[false, 'Off (whole days)'], [true, 'Free + Post-call']].map(([val, lbl]) => (
                  <button
                    key={String(val)}
                    type="button"
                    onClick={() => saveFreeRows(val)}
                    className={`px-4 h-[34px] rounded-lg text-xs font-bold transition-all ${freeRows === val ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                  >{lbl}</button>
                ))}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest">
              {freeRowsStatus === 'saved' && <span className="text-emerald-600">Saved</span>}
              {freeRowsStatus === 'error' && <span className="text-rose-500">Save failed</span>}
            </div>
          </Card>

          {/* Leave in the workload counter. Deliberately its own card rather than
              a row inside the metric card: it changes what a number MEANS, not
              which number is shown. It configures the SELECTED metric — the two
              are stored independently, so switching above does not disturb the
              other one's setting. */}
          {(() => {
            const isHours = counterMetric === 'hours';
            const cfg = leaveCounter[counterMetric] || { enabled: false, value: isHours ? 8 : 1 };
            const unitLabel = isHours ? 'h / leave day' : 'shifts / leave day';
            const max = leaveMax[counterMetric] ?? (isHours ? 24 : 10);
            const shown = leaveDraft === null ? cfg.value : leaveDraft;
            return (
              <Card>
                <div className="flex items-start justify-between gap-6">
                  <div className="min-w-0">
                    <h4 className="text-sm font-black text-slate-900 tracking-tight">
                      Count Leave as {isHours ? 'Hours' : 'Shifts'}
                    </h4>
                    <p className="text-xs text-slate-500 mt-1 font-medium leading-relaxed">
                      Adds each leave day to a person&rsquo;s {isHours ? 'hours' : 'shifts'} on the staff view and
                      Excel. Half days count half. Display only — it doesn&rsquo;t affect solving.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                      {[[false, 'Exclude'], [true, 'Include']].map(([val, lbl]) => (
                        <button
                          key={String(val)}
                          type="button"
                          disabled={leaveSaving}
                          onClick={() => saveLeaveCounter({ enabled: val })}
                          className={`px-4 h-[34px] rounded-lg text-xs font-bold transition-all ${cfg.enabled === val ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >{lbl}</button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min={leaveMin}
                        max={max}
                        step="0.5"
                        value={shown}
                        disabled={leaveSaving}
                        onChange={(e) => setLeaveDraft(e.target.value === '' ? '' : Number(e.target.value))}
                        onBlur={() => {
                          if (leaveDraft === null) return;
                          const n = Number(leaveDraft);
                          if (!Number.isFinite(n) || n < leaveMin || n > max) {
                            setLeaveStatus('error');
                            setLeaveDraft(null);      // snap back to the stored value
                            return;
                          }
                          if (n === cfg.value) { setLeaveDraft(null); return; }
                          saveLeaveCounter({ value: n });
                        }}
                        className="w-20 h-[34px] px-2 text-center text-xs font-bold tabular-nums bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-slate-400"
                      />
                      <span className="text-xs font-bold text-slate-400 whitespace-nowrap">{unitLabel}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest">
                  <span className="text-slate-300">
                    {cfg.enabled
                      ? `1 leave day = ${cfg.value} ${isHours ? 'h' : (cfg.value === 1 ? 'shift' : 'shifts')}`
                      : 'Not counted'}
                  </span>
                  {leaveStatus === 'saved' && <span className="text-emerald-600">Saved</span>}
                  {leaveStatus === 'error' && <span className="text-rose-500">Save failed</span>}
                </div>
              </Card>
            );
          })()}

          {/* Read-only banner: constraints are managed by the maintenance team */}
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl">
            <ShieldAlert className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <p className="text-[10px] font-bold text-slate-500 tracking-wide">
              Rules are set by the maintenance team and can&rsquo;t be changed here.
            </p>
          </div>

          {/* Loading state */}
          {constraintsLoading && (
            <div className="py-24 text-center space-y-3">
              <RefreshCw className="w-6 h-6 text-slate-300 animate-spin mx-auto" />
              <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">Loading rules…</p>
            </div>
          )}

          {/* Error state */}
          {!constraintsLoading && constraintsError && (
            <div className="py-24 text-center space-y-3">
              <div className="w-10 h-10 bg-rose-50 rounded-full flex items-center justify-center mx-auto">
                <ShieldAlert className="w-5 h-5 text-rose-300" />
              </div>
              <p className="text-[10px] font-bold text-rose-400 uppercase tracking-widest">{constraintsError}</p>
            </div>
          )}

          {/* Loaded content */}
          {!constraintsLoading && !constraintsError && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Main Dashboard (Clinical) */}
              <div className="space-y-4">
                <div className="mb-4 px-1 flex items-center justify-between">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Clinical Rules</h4>
                  <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest">{clinicalConstraints.length} Rules</span>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {clinicalConstraints.map(opt => (
                    <ConstraintCard key={opt.id} opt={opt} isActive={opt.enabled} readOnly />
                  ))}
                  {clinicalConstraints.length === 0 && (
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic py-8 text-center">No clinical rules configured.</p>
                  )}
                </div>
              </div>

              {/* On-Call Roster */}
              <div className="space-y-4">
                <div className="mb-4 px-1 flex items-center justify-between">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">On-Call Rules</h4>
                  <span className="text-[8px] font-bold text-slate-300 uppercase tracking-widest">{onCallConstraints.length} Rules</span>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {onCallConstraints.map(opt => (
                    <ConstraintCard key={opt.id} opt={opt} isActive={opt.enabled} readOnly />
                  ))}
                  {onCallConstraints.length === 0 && (
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic py-8 text-center">No on-call rules configured.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. SYSTEM MAINTENANCE SECTION */}
      {viewMode === 'maintenance' && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center gap-4 px-2">
            <div className="p-2.5 bg-rose-50 rounded-xl border border-rose-100 text-rose-500 shadow-sm"><Database className="w-5 h-5" /></div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 tracking-tight leading-none text-rose-950">System Maintenance</h3>
              <p className="text-[11px] text-rose-400 mt-1.5 font-medium uppercase tracking-wider font-bold">These can&rsquo;t be undone</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MaintenanceCard
              title="Clear All Leave" desc="Removes everyone's leave, on-call wishes and blackout dates."
              icon={RefreshCw} color="rose" onClick={() => handleGlobalReset('availability')} isLoading={isClearing === 'availability'}
            />
            <MaintenanceCard 
              title="Clear On-Call Roster" desc="Removes every month's on-call roster."
              icon={Trash2} color="rose" onClick={() => handleGlobalReset('on-call')} isLoading={isClearing === 'on-call'}
            />
            <MaintenanceCard 
              title="Download Backup" desc="Saves all the data as one file."
              icon={Download} color="slate" onClick={() => {
                downloadFile('/data/data.json', 'data.json')
                  .catch((err) => console.error('Data export failed', err));
              }}
            />
            <MaintenanceCard 
              title="Restore Backup" desc="Not available yet."
              icon={Upload} color="slate" onClick={() => toast.info("Restoring from a backup file isn't available yet. (Clinical Operations' upload imports spreadsheets, not backups.)")}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// --- SUB-COMPONENTS ---

// ConstraintCard renders a single constraint's label, hard/soft badge,
// description, and enabled status.
//
// readOnly (default true on this page): the card is a non-interactive status
// indicator — the on/off pill still shows the enabled state, but there is no
// click handler and no hover affordance, since constraints can only be changed
// in the maintenance admin GUI. The legacy interactive `toggle` prop is still
// accepted for backward compatibility with any other caller of this component.
const ConstraintCard = ({ opt, isActive, toggle, readOnly = false }) => {
  const Wrapper = readOnly ? 'div' : 'button';
  return (
    <Wrapper
      onClick={readOnly ? undefined : toggle}
      title={readOnly ? 'Managed by the maintenance team' : undefined}
      className={`w-full text-left p-3 rounded-xl border transition-all duration-200 flex flex-col gap-1 relative overflow-hidden group ${
        readOnly ? 'cursor-default' : ''
      } ${
        isActive ? 'bg-white border-slate-200' : `bg-slate-50 border-slate-100 ${readOnly ? '' : 'hover:border-slate-200'} opacity-70 ${readOnly ? '' : 'hover:opacity-100'}`
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`px-1.5 py-0.5 rounded text-[8px] font-bold tracking-tighter ${
            opt.type === 'hard' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'
          }`}>
            {opt.type.toUpperCase()}
          </div>
          <span className={`text-xs font-bold tracking-tight ${isActive ? 'text-slate-900' : 'text-slate-500'}`}>{opt.label}</span>
        </div>
        {/* On/off pill: reflects enabled status. Non-interactive when readOnly. */}
        <div className={`w-8 h-4 rounded-full relative transition-all duration-200 ${isActive ? 'bg-[var(--primary)]' : 'bg-slate-200'}`}>
          <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-all duration-200 ${isActive ? 'right-0.5' : 'left-0.5'}`} />
        </div>
      </div>
      {opt.desc && <p className={`text-[11px] font-medium leading-snug pl-1 transition-colors ${isActive ? 'text-slate-500' : 'text-slate-400'}`}>{opt.desc}</p>}
    </Wrapper>
  );
};

const MaintenanceCard = ({ title, desc, icon: Icon, color, onClick, isLoading }) => (
  <button 
    onClick={onClick} disabled={isLoading}
    className={`p-6 rounded-[28px] border-2 transition-all group flex flex-col text-left h-full ${
      color === 'rose' 
        ? 'bg-rose-50/30 border-rose-100 hover:bg-rose-50 hover:border-rose-300 hover:shadow-xl' 
        : 'bg-slate-50/50 border-slate-100 hover:bg-white hover:border-slate-300 hover:shadow-xl'
    }`}
  >
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 ${
      color === 'rose' ? 'bg-white text-rose-500 shadow-md border border-rose-50' : 'bg-white text-slate-400 shadow-md border border-slate-50'
    }`}>
      {isLoading ? <RefreshCw className="w-6 h-6 animate-spin" /> : <Icon className="w-6 h-6" />}
    </div>
    <h4 className={`text-sm font-bold mb-2 uppercase tracking-tight ${color === 'rose' ? 'text-rose-900' : 'text-slate-900'}`}>{title}</h4>
    <p className="text-[10px] font-bold text-slate-400 leading-relaxed uppercase tracking-wider">{desc}</p>
  </button>
);

export default SettingsTab;
